import type {
  RegisterInput,
  LoginInput,
  CreateTenantInput,
  AuthResponse,
  CurrentUser,
  TenantMembership,
  ApiResponse,
} from '@maker-toolchest/shared';
import { api, ApiError } from './api.js';
import { useAuthStore, isTokenExpired } from '@/stores/auth.js';
import { preferencesCache } from './preferencesCache.js';

const AUTH_BASE = '/auth';

/**
 * Register a new user
 */
export async function register(input: RegisterInput): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>(`${AUTH_BASE}/register`, input);
  if (!response.success || !response.data) {
    throw new ApiError(response as ApiResponse<never>);
  }

  // Store tokens and fetch user
  useAuthStore.getState().setTokens(response.data.tokens);
  await fetchCurrentUser();

  return response.data;
}

/**
 * Login with email/password
 */
export async function login(input: LoginInput): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>(`${AUTH_BASE}/login`, input);
  if (!response.success || !response.data) {
    throw new ApiError(response as ApiResponse<never>);
  }

  // Store tokens and fetch user
  useAuthStore.getState().setTokens(response.data.tokens);
  await fetchCurrentUser();

  return response.data;
}

/**
 * Logout (revoke refresh token)
 */
export async function logout(): Promise<void> {
  const { refreshToken, user, logout: clearAuth } = useAuthStore.getState();

  try {
    if (refreshToken) {
      await api.post(`${AUTH_BASE}/logout`, { refreshToken });
    }
  } catch {
    // Ignore errors during logout
  } finally {
    // Clear preference cache
    if (user?.id) {
      preferencesCache.clear(user.id);
    }

    clearAuth();
  }
}

/**
 * Refresh access token
 */
export async function refreshAccessToken(): Promise<boolean> {
  const { refreshToken, setTokens, logout: clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    return false;
  }

  try {
    const response = await api.post<AuthResponse>(`${AUTH_BASE}/refresh`, {
      refreshToken,
    });

    if (!response.success || !response.data) {
      clearAuth();
      return false;
    }

    setTokens(response.data.tokens);
    return true;
  } catch {
    clearAuth();
    return false;
  }
}

/**
 * Fetch current user info
 */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const { setUser, setLoading, setInitialized } = useAuthStore.getState();

  try {
    setLoading(true);

    // Check if we need to refresh the token
    if (isTokenExpired()) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        setUser(null);
        setInitialized();
        return null;
      }
    }

    const response = await api.get<CurrentUser>(`${AUTH_BASE}/me`);
    if (!response.success || !response.data) {
      setUser(null);
      setInitialized();
      return null;
    }

    setUser(response.data);
    setInitialized();
    return response.data;
  } catch {
    setUser(null);
    setInitialized();
    return null;
  }
}

/**
 * Create a new tenant/workspace
 */
export async function createTenant(input: CreateTenantInput): Promise<TenantMembership> {
  const response = await api.post<TenantMembership>(`${AUTH_BASE}/tenants`, input);
  if (!response.success || !response.data) {
    throw new ApiError(response as ApiResponse<never>);
  }

  // Refresh user to get updated tenant list
  await fetchCurrentUser();

  return response.data;
}

/**
 * Exchange OAuth token for access/refresh tokens
 */
async function exchangeOAuthToken(exchangeToken: string): Promise<boolean> {
  try {
    const response = await api.post<AuthResponse>('/auth/oauth/exchange', { exchangeToken });

    if (!response.success || !response.data) {
      return false;
    }

    // Store tokens
    useAuthStore.getState().setTokens(response.data.tokens);

    // Fetch user info
    await fetchCurrentUser();

    return true;
  } catch (error) {
    console.error('OAuth exchange failed:', error);
    return false;
  }
}

/**
 * Initialize auth from URL params (OAuth callback)
 */
export async function initAuthFromCallback(): Promise<boolean> {
  // Check for exchange token in query params
  const params = new URLSearchParams(window.location.search);
  const exchangeToken = params.get('exchangeToken');

  if (!exchangeToken) {
    return false;
  }

  // Clear URL params
  window.history.replaceState(null, '', window.location.pathname);

  // Exchange the token
  return await exchangeOAuthToken(exchangeToken);
}

/**
 * Get OAuth login URL
 */
export function getOAuthUrl(provider: 'google' | 'microsoft'): string {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  return `${apiUrl}/auth/${provider}`;
}

/**
 * Initialize auth on app load
 */
export async function initAuth(): Promise<void> {
  const { accessToken, setInitialized, setLoading } = useAuthStore.getState();

  // Check for OAuth callback
  if (await initAuthFromCallback()) {
    return;
  }

  // If we have an access token, try to fetch the user
  if (accessToken) {
    await fetchCurrentUser();
    return;
  }

  // No token, mark as initialized
  setLoading(false);
  setInitialized();
}
